import React, { useState } from 'react';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Loading from '../Loading/Loading';
import ProfileIndex from '../../pages/Profile/ProfileIndex';
import SelectionPerformer from '../../pages/SelectionPerformer';
import { transferUserEmployers } from '../../redux/actions/user/transferUserEmployers';
import { removeUserEmployers } from '../../redux/actions/user/removeUserEmployers';
import { resetRemoveUserEmployers, resetTransferUserEmployers } from '../../redux/slices/user.slice';

const toArray = (value) => (Array.isArray(value) ? value : []);

const parseErrorMessage = (error) => {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.detail === 'string') {
    return error.detail;
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return 'Не удалось загрузить распределение контрагентов';
};

const getErrorMessage = (error, fallbackText) => {
  if (!error) {
    return fallbackText;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.detail === 'string') {
    return error.detail;
  }

  if (Array.isArray(error?.detail) && error.detail.length) {
    return error.detail.join(', ');
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  const firstFieldError = Object.values(error).find((value) => (Array.isArray(value) && value.length > 0) || typeof value === 'string');

  if (Array.isArray(firstFieldError)) {
    return firstFieldError[0];
  }

  if (typeof firstFieldError === 'string') {
    return firstFieldError;
  }

  return fallbackText;
};

const EmployersList = ({ employers, ownerUserId, transferToUserId, onOpenUser, onDetachEmployer, onTransferEmployer }) => {
  const list = toArray(employers);

  if (!list.length) {
    return <p className="text-sm text-gray-500 mt-2">Контрагенты отсутствуют</p>;
  }

  return (
    <ul className="mt-2 max-h-[260px] overflow-auto pr-1 space-y-1">
      {list.map((employer) => (
        <li key={employer?.id} className="text-sm text-gray-700 border border-gray-200 rounded px-2 py-1 cursor-pointer hover:bg-gray-50 flex items-center justify-between gap-2">
          <button
            type="button"
            className="text-left min-w-0 flex-1"
            onClick={() => {
              if (onOpenUser && employer?.id) {
                onOpenUser(employer.id);
              }
            }}>
            <span className="truncate inline-block max-w-full align-bottom">
              {employer?.name || 'Без названия'} <span className="text-gray-400">#{employer?.id ?? '—'}</span>
            </span>
          </button>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              aria-label="Открепить"
              className="p-0 m-0 bg-transparent"
              onClick={(event) => {
                event.stopPropagation();
                onDetachEmployer?.(employer, ownerUserId, transferToUserId);
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path fill="rgb(239 68 68 / var(--tw-bg-opacity))" d="M7 11v2h10v-2z"></path>
              </svg>
            </button>
            <button
              type="button"
              aria-label="Передать"
              className="p-0 m-0 bg-transparent"
              onClick={(event) => {
                event.stopPropagation();
                onTransferEmployer?.(employer, ownerUserId);
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="21.34" height="12" viewBox="0 0 16 9">
                <path fill="rgb(0 178 164 / var(--tw-bg-opacity))" d="M12.5 5h-9c-.28 0-.5-.22-.5-.5s.22-.5.5-.5h9c.28 0 .5.22.5.5s-.22.5-.5.5" />
                <path fill="rgb(0 178 164 / var(--tw-bg-opacity))" d="M10 8.5a.47.47 0 0 1-.35-.15c-.2-.2-.2-.51 0-.71l3.15-3.15l-3.15-3.15c-.2-.2-.2-.51 0-.71s.51-.2.71 0l3.5 3.5c.2.2.2.51 0 .71l-3.5 3.5c-.1.1-.23.15-.35.15Z" />
              </svg>
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const SubordinateNode = ({ node, parentUserId, onOpenUser, onDetachEmployer, onTransferEmployer, onEditUserClick }) => {
  const user = node?.user || {};
  const employers = toArray(node?.personal_employers);
  const children = toArray(node?.subordinates);
  const canEditUser = Number.isFinite(Number(user?.id));

  return (
    <div className="mt-3 pl-4 border-l border-gray-300">
      <div className="rounded-md border border-gray-200 p-3 bg-white">
        <div
          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 ${canEditUser ? 'cursor-pointer rounded transition-colors hover:bg-gray-50 -mx-1 px-1' : ''}`}
          onClick={() => {
            if (canEditUser) {
              onEditUserClick?.(Number(user.id));
            }
          }}>
          <p className="font-medium text-gray-900">{user?.full_name || 'Пользователь'}</p>
          <p className="text-sm text-gray-600">Контрагентов: {employers.length}</p>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          ID: {user?.id ?? '—'}
          {Number.isFinite(Number(user?.granted_limit)) ? ` | Выданный лимит: ${Number(user.granted_limit)}` : ''}
        </p>
        <EmployersList employers={employers} ownerUserId={user?.id} transferToUserId={parentUserId} onOpenUser={onOpenUser} onDetachEmployer={onDetachEmployer} onTransferEmployer={onTransferEmployer} />
      </div>

      {children.map((child, index) => (
        <SubordinateNode key={child?.user?.id || `child-${index}`} node={child} parentUserId={user?.id} onOpenUser={onOpenUser} onDetachEmployer={onDetachEmployer} onTransferEmployer={onTransferEmployer} onEditUserClick={onEditUserClick} />
      ))}
    </div>
  );
};

function EmployersDistributionView({ data, loading, error, onRefreshDistribution, isManageSuperAdminView = false }) {
  const [openUser, setOpenUser] = useState(null);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [isSelectUserModalOpen, setIsSelectUserModalOpen] = useState(false);
  const [transferContext, setTransferContext] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rootUser = data?.user || null;
  const personalEmployers = toArray(data?.personal_employers);
  const subordinates = toArray(data?.subordinates);

  const refreshDistribution = () => {
    if (typeof onRefreshDistribution === 'function') {
      onRefreshDistribution();
    }
  };

  const runTransfer = async ({ userId, fromUserId, employers, successMessage, fallbackErrorMessage }) => {
    setIsTransferLoading(true);
    const transferAction = await dispatch(
      transferUserEmployers({
        userId: Number(userId),
        from_user_id: Number(fromUserId),
        employers,
      }),
    );
    setIsTransferLoading(false);

    if (transferUserEmployers.fulfilled.match(transferAction)) {
      toast.success(successMessage, { theme: 'colored', autoClose: '3000' });
      dispatch(resetTransferUserEmployers());
      refreshDistribution();
      return true;
    }

    toast.error(getErrorMessage(transferAction.payload, fallbackErrorMessage), { theme: 'colored', autoClose: '3000' });
    dispatch(resetTransferUserEmployers());
    return false;
  };

  const handleDetachEmployer = async (employer, fromUserId, transferToUserId) => {
    if (!Number.isFinite(Number(fromUserId)) || !Number.isFinite(Number(employer?.id))) {
      return;
    }

    const result = await Swal.fire({
      title: 'Вы действительно хотите открепить контрагента?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsTransferLoading(true);
    const transferTargetUserId = Number(transferToUserId);
    const hasTransferTarget = Number.isFinite(transferTargetUserId) && transferTargetUserId !== 1 && transferTargetUserId !== Number(fromUserId);
    const detachAction = await dispatch(
      hasTransferTarget
        ? transferUserEmployers({
            userId: transferTargetUserId,
            from_user_id: Number(fromUserId),
            employers: [Number(employer.id)],
          })
        : removeUserEmployers({
            userId: Number(fromUserId),
            employers: [Number(employer.id)],
          }),
    );
    setIsTransferLoading(false);

    if ((hasTransferTarget && transferUserEmployers.fulfilled.match(detachAction)) || (!hasTransferTarget && removeUserEmployers.fulfilled.match(detachAction))) {
      toast.success('Контрагент успешно откреплен', { theme: 'colored', autoClose: '3000' });
      if (hasTransferTarget) {
        dispatch(resetTransferUserEmployers());
      } else {
        dispatch(resetRemoveUserEmployers());
      }
      refreshDistribution();
      return;
    }

    toast.error(getErrorMessage(detachAction.payload, 'Не удалось открепить контрагента'), { theme: 'colored', autoClose: '3000' });
    if (hasTransferTarget) {
      dispatch(resetTransferUserEmployers());
    } else {
      dispatch(resetRemoveUserEmployers());
    }
  };

  const handleOpenTransferModal = (employer, fromUserId) => {
    if (!Number.isFinite(Number(fromUserId)) || !Number.isFinite(Number(employer?.id))) {
      return;
    }

    setTransferContext({
      fromUserId: Number(fromUserId),
      employerId: Number(employer.id),
      employerName: employer?.name || 'Без названия',
    });
    setIsSelectUserModalOpen(true);
  };

  const handleEditUserClick = (userId) => {
    if (!Number.isFinite(Number(userId))) {
      return;
    }

    navigate(`/edit/user/${Number(userId)}`);
  };

  const handleTransferToUser = async (selectedUserIds) => {
    const targetUserId = Number(selectedUserIds?.[0]);

    if (!transferContext || !Number.isFinite(targetUserId)) {
      toast.error('Выберите пользователя', { theme: 'colored', autoClose: '3000' });
      return;
    }

    if (targetUserId === Number(transferContext.fromUserId)) {
      toast.error('Выберите другого пользователя', { theme: 'colored', autoClose: '3000' });
      return;
    }

    const isSuccess = await runTransfer({
      userId: targetUserId,
      fromUserId: transferContext.fromUserId,
      employers: [transferContext.employerId],
      successMessage: 'Контрагент успешно прикреплен',
      fallbackErrorMessage: 'Не удалось прикрепить контрагента',
    });

    if (isSuccess) {
      setIsSelectUserModalOpen(false);
      setTransferContext(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full py-8 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-500">{parseErrorMessage(error)}</div>;
  }

  if (!rootUser) {
    return <div className="text-sm text-gray-500">Нет данных для отображения</div>;
  }

  return (
    <div className="w-full max-w-[1163px] mx-auto p-2">
      {isTransferLoading && (
        <div className="fixed t-0 l-0 r-0 b-0 z-50 w-[100vw] h-[100vh]">
          <div className="fixed w-[32px] h-[32px] right-4 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-white">
            <Loading />
          </div>
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-white p-4 mb-3">
        <p className="text-lg font-medium text-gray-900">{rootUser?.full_name || 'Пользователь'}</p>
        <p className="text-sm text-gray-600 mt-1">
          Лимит: {isManageSuperAdminView ? '∞' : Number.isFinite(Number(rootUser?.original_quota)) ? Number(rootUser.original_quota) : '—'} | Доступно: {isManageSuperAdminView ? '∞' : Number.isFinite(Number(rootUser?.max_employers)) ? Number(rootUser.max_employers) : '—'} | Контрагенты
          пользователей: {Number.isFinite(Number(rootUser?.granted_to_subordinates)) ? Number(rootUser.granted_to_subordinates) : '—'}
        </p>
      </div>

      {!isManageSuperAdminView && (
        <div className="rounded-md border border-gray-200 bg-white p-4 mb-3">
          <p className="text-base font-medium text-gray-900">Прикрепленные контрагенты ({personalEmployers.length})</p>
          <p className="text-xs text-gray-500 mt-1">Принадлежат самому пользователю</p>
          <EmployersList employers={personalEmployers} ownerUserId={rootUser?.id} onOpenUser={setOpenUser} onDetachEmployer={handleDetachEmployer} onTransferEmployer={handleOpenTransferModal} />
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-white p-4">
        <p className="text-base font-medium text-gray-900">Контрагенты пользователей</p>
        {!subordinates.length ? (
          <p className="text-sm text-gray-500 mt-2">Подчиненные с контрагентами отсутствуют</p>
        ) : (
          subordinates.map((node, index) => <SubordinateNode key={node?.user?.id || `root-${index}`} node={node} parentUserId={rootUser?.id} onOpenUser={setOpenUser} onDetachEmployer={handleDetachEmployer} onTransferEmployer={handleOpenTransferModal} onEditUserClick={handleEditUserClick} />)
        )}
      </div>

      <Modal
        className="select-user-modal"
        style={{ height: 'calc(100vh - 40px)' }}
        title={``}
        centered
        open={openUser}
        width={'100%'}
        onCancel={() => {
          setOpenUser(null);
        }}
        footer={['']}>
        <ProfileIndex userId={openUser} isEditComment />
      </Modal>

      <Modal
        className="select-user-modal"
        style={{ height: 'calc(100vh - 40px)' }}
        title={``}
        centered
        destroyOnClose
        open={isSelectUserModalOpen}
        width={'100%'}
        onCancel={() => {
          if (isTransferLoading) {
            return;
          }
          setIsSelectUserModalOpen(false);
          setTransferContext(null);
        }}
        footer={['']}>
        <SelectionPerformer
          mode="users-transfer"
          title="Выберите пользователя"
          subtitle={`Выбранному пользователю будет прикреплен контрагент ${transferContext?.employerName || ''}`}
          sliceName="user"
          actionName="getUsers"
          fetchParams={{ isAll: true, type: 'staff', is_active: true }}
          submitButtonText="Прикрепить"
          selectionVisual="check-green"
          singleSelection
          onSubmitSelection={handleTransferToUser}
          isSubmitLoading={isTransferLoading}
          onClose={() => {
            setIsSelectUserModalOpen(false);
            setTransferContext(null);
          }}
        />
      </Modal>
    </div>
  );
}

export default EmployersDistributionView;
